export const SITE_NAME = 'AviaGov';
export const SITE_URL = 'https://www.aviagov.aero';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/AviaGov.svg`;
export const FLYMATE_OG_IMAGE = `${SITE_URL}/flymate-og-card.png`;

const BASE_KEYWORDS = [
  'aviation safety management system',
  'ICAO Annex 15',
  'ICAO Annex 19',
  'EUROCONTROL',
  'learning management systems',
  'AIM',
  'AIS',
  'ANSP',
  'civil aviation',
  'aeronautical information management',
  'eAIP',
  'CAA',
  'aviation compliance',
];

const HOME_FAQ = [
  {
    question: 'What is AviaGov?',
    answer:
      'AviaGov is an enterprise aviation platform designed specifically for Civil Aviation Authorities (CAAs) and ANSPs. It integrates safety, training, procedure design, and aeronautical publication into one high-performance loop. Operators and ATOs typically start with SMS + LMS, while authorities start with eAIP to modernize publication.',
  },
  {
    question: 'How does AviaGov improve operational efficiency?',
    answer:
      'By connecting traditionally siloed departments, AviaGov accelerates incident-to-publication workflows, eliminating manual handoffs and fragmented audit trails. Operators achieve automated compliance tracking, while authorities streamline complex publication cycles.',
  },
  {
    question: 'Does AviaGov support local data sovereignty?',
    answer:
      'Yes. For authorities with strict requirements, we offer Private Cloud and On-Premise deployment models to ensure your aeronautical data remains within national infrastructure.',
  },
];

const EAIP_FAQ = [
  {
    question: 'How does AI NOTAM generation work?',
    answer:
      'The AviaGov AI engine reads safety findings or procedure changes and automatically formats them into the strict ICAO NOTAM string format, reducing the time from 30 minutes to just 3 minutes with 90% fewer manual errors.',
  },
  {
    question: 'Can we automate the translation of the AIP?',
    answer:
      'Yes. Using our fine-tuned aviation LLM, we can translate complex aeronautical text between ICAO languages in minutes rather than weeks, preserving all technical terminology and formatting.',
  },
  {
    question: 'Does it support the full AIRAC cycle?',
    answer:
      'Absolutely. AviaGov manages the 28-day/56-day AIRAC cycles with automated publication triggers and data validation locks.',
  },
];

const SMS_FAQ = [
  {
    question: 'Is the SMS module ECCAIRS 2 compatible?',
    answer:
      'Yes. AviaGov SMS supports the full taxonomy required for ECCAIRS 2 reporting, making it easy to share safety data with EASA and ICAO.',
  },
  {
    question: 'Can staff report safety issues anonymously?',
    answer:
      'Yes. We support Just Culture principles with fully anonymous reporting paths that strip metadata while preserving the critical safety details.',
  },
  {
    question: 'Does it support Bow-Tie risk modeling?',
    answer:
      'Yes, our SMS includes a built-in interactive Bow-Tie designer to help visualize threats, barriers, and recovery paths for safety risks.',
  },
];

const LMS_FAQ = [
  {
    question: 'Can we track ATCO competency within the LMS?',
    answer:
      'Yes. AviaGov LMS includes specialized competency frameworks for Air Traffic Controllers, including unit endorsements and medical rating tracking.',
  },
  {
    question: 'Does the platform support SCORM or xAPI?',
    answer:
      'Yes, our LMS is fully compliant with modern e-learning standards, allowing you to import your existing aviation courseware.',
  },
  {
    question: 'How are exams secured?',
    answer:
      'We offer randomized question banks and optional browser-lockdown proctoring for high-stakes certification exams.',
  },
];

const PANS_OPS_FAQ = [
  {
    question:
      'Does the tool support PANS-OPS obstacle assessment surfaces (OAS)?',
    answer:
      'Yes, our engine automatically constructs OAS, CRM, and OLS surfaces based on the latest ICAO Doc 8168 parameters.',
  },
  {
    question: 'Can we import high-resolution terrain data?',
    answer:
      'Absolutely. AviaGov supports GeoTIFF, DTED, and other standard terrain formats to ensure precise clearance calculations.',
  },
  {
    question: 'Is the final procedure design exported to eAIP?',
    answer:
      'Yes, designs can be exported as AIXM 5.1 features, which flow directly into our eAIP module for publication.',
  },
];

const COMPLIANCE_FAQ = [
  {
    question: 'Is AviaGov eAIP fully compliant with ICAO Annex 15?',
    answer:
      'Yes. AviaGov is architected specifically to meet and exceed ICAO Annex 15 standards for Aeronautical Information Services. It manages the entire AIS data chain from origination to dissemination with full metadata traceability.',
  },
  {
    question: 'Does the platform support EUROCONTROL Specification 3.0?',
    answer:
      'Absolutely. We strictly follow the EUROCONTROL Specification for the Electronic Aeronautical Information Publication (eAIP), including XML/XHTML publication structures and the standardized AIRAC lifecycle.',
  },
  {
    question: 'How does AviaGov ensure Data Quality (ADQ)?',
    answer:
      'AviaGov enforces ADQ requirements (EU Reg 73/2010) through automated validation rules, electronic data exchange (AIXM 5.1), and an immutable audit trail that tracks every change made to aeronautical features.',
  },
  {
    question: 'What security certifications does AviaGov hold?',
    answer:
      "AviaGov's cloud infrastructure is hosted in SOC 2 Type II and ISO 27001 certified data centers. We implement RBAC (Role-Based Access Control) and end-to-end encryption for all mission-critical datasets.",
  },
];

const TRY_AI_FAQ = [
  {
    question: 'What is FlyMate aviation AI?',
    answer:
      'FlyMate is an aviation AI assistant for ANSPs, civil aviation authorities, AIM and AIS teams, and other regulated aviation operators. It answers aviation and ICAO questions from a grounded knowledge base instead of inventing facts.',
  },
  {
    question: 'Can ANSPs use FlyMate as an AI assistant?',
    answer:
      'Yes. FlyMate is designed as AI for ANSP workflows such as ICAO research, AIM and eAIP support, aviation message review, and procedural publication context, while leaving operational decisions and approvals with humans.',
  },
  {
    question: 'What can FlyMate help aviation teams with?',
    answer:
      'FlyMate can help with ICAO references, aeronautical information management, eAIP publication questions, PANS-OPS context, aviation compliance topics, and review or drafting support for aviation message families such as NOTAM, ASHTAM, SNOWTAM, METAR, TAF, SIGMET, and AIRMET.',
  },
  {
    question: 'Does FlyMate invent operational facts or procedures?',
    answer:
      'No. FlyMate is meant to refuse unsupported claims, avoid inventing operational data or procedures, and answer only when grounded evidence is available from the knowledge base.',
  },
];

export const SEO_ROUTES = [
  {
    path: '/',
    title: 'Enterprise Aviation Software for CAAs, ANSPs, Operators, and ATOs',
    description:
      'Manage SMS, LMS, eAIP, PANS-OPS, and U-Space governance in one regulated aviation platform for authorities, ANSPs, airlines, operators, and training organizations.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'enterprise aviation platform',
      'digital aeronautical information',
      'safety management system',
      'PANS-OPS',
    ],
    faq: HOME_FAQ,
  },
  {
    path: '/modules/eaip',
    title: 'eAIP Software for Civil Aviation Authorities and ANSPs',
    description:
      'ICAO Annex 15 aligned eAIP software for AIM teams managing AIP amendments, NOTAM preparation, AIRAC cycles, validation, and public aeronautical publication.',
    ogType: 'product',
    keywords: [
      ...BASE_KEYWORDS,
      'electronic AIP',
      'AIRAC cycle automation',
      'NOTAM automation',
      'AIXM',
    ],
    faq: EAIP_FAQ,
  },
  {
    path: '/modules/sms',
    title: 'Aviation Safety Management System Software',
    description:
      'ICAO Annex 19 aligned SMS software for aviation operators, airlines, CAAs, and ANSPs managing hazards, risk, CAPA, SPIs, audits, and safety evidence.',
    ogType: 'product',
    keywords: [
      ...BASE_KEYWORDS,
      'aviation safety management system',
      'ICAO Annex 19 compliance',
      'ECCAIRS 2',
      'safety reporting',
    ],
    faq: SMS_FAQ,
  },
  {
    path: '/modules/lms',
    title: 'Aviation LMS Software for Training and Qualification Governance',
    description:
      'Aviation LMS software for airlines, ANSPs, operators, ATOs, and authorities managing course delivery, recurrence, qualifications, ATCO objectives, and audit-ready training records.',
    ogType: 'product',
    keywords: [
      ...BASE_KEYWORDS,
      'learning management systems',
      'aviation training',
      'ATCO competency tracking',
      'SCORM',
      'xAPI',
    ],
    faq: LMS_FAQ,
  },
  {
    path: '/modules/pans-ops',
    title: 'PANS-OPS Procedure Design Software',
    description: 'Procedure design and obstacle assessment software for authorities, ANSPs, and procedure designers working with ICAO Doc 8168, validation, and eAIP publication workflows.',
    ogType: 'product',
    keywords: [
      ...BASE_KEYWORDS,
      'PANS-OPS',
      'procedure design',
      'obstacle assessment',
      'ICAO Doc 8168',
    ],
    faq: PANS_OPS_FAQ,
  },
  {
    path: '/modules/ai-assistant',
    title: 'Aviation AI Assistant for ICAO, AIM, eAIP, SMS, and PANS-OPS',
    description:
      'AviaGov\'s aviation AI assistant supports ICAO-grounded compliance work across SMS, LMS, eAIP, AIM, AIS, and PANS-OPS while keeping operational decisions under human responsibility.',
    ogType: 'product',
    keywords: [
      ...BASE_KEYWORDS,
      'aviation AI',
      'aviation AI assistant',
      'AI for aviation',
      'FlyMate',
      'FlyMate aviation AI',
      'AI for ANSP',
      'ANSP AI',
      'AI for CAA',
      'AI for civil aviation authority',
      'AI for AIM',
      'AI for AIS',
      'eAIP AI assistant',
      'AI compliance assistant',
      'aviation compliance assistant',
      'aviation compliance support',
      'ICAO guidance assistant',
      'ICAO AI assistant',
      'regulatory interpretation support',
      'grounded aviation AI',
    ],
  },
  {
    path: '/flymate',
    title: 'FlyMate Aviation AI Assistant for ICAO, AIM, eAIP, and Compliance',
    description:
      'FlyMate is AviaGov\'s aviation AI assistant for grounded ICAO, AIM, AIS, eAIP, PANS-OPS, compliance, and aviation message support with source-backed answers for ANSPs, CAAs, and regulated aviation teams.',
    canonical: `${SITE_URL}/flymate`,
    ogType: 'website',
    ogImage: FLYMATE_OG_IMAGE,
    keywords: [
      ...BASE_KEYWORDS,
      'FlyMate',
      'aviation AI',
      'FlyMate aviation AI assistant',
      'aviation AI workspace',
      'ICAO AI assistant',
      'AI for ANSP',
      'ANSP AI',
      'AI for civil aviation authority',
      'CAA AI assistant',
      'AI for AIM',
      'AI for AIS',
      'eAIP AI assistant',
      'aviation compliance assistant',
      'aviation chatbot',
      'aviation knowledge base',
      'aeronautical messages AI',
      'grounded aviation AI',
    ],
    faq: TRY_AI_FAQ,
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'FlyMate',
        url: `${SITE_URL}/flymate`,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        image: FLYMATE_OG_IMAGE,
        description:
          'A grounded aviation AI assistant for ICAO, AIM, eAIP, PANS-OPS, compliance, and aviation message support.',
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: SITE_URL,
          logo: `${SITE_URL}/AviaGov.svg`,
        },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          availability: 'https://schema.org/OnlineOnly',
          url: `${SITE_URL}/flymate`,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'FlyMate', item: `${SITE_URL}/flymate` },
        ],
      },
    ],
  },
  {
    path: '/modules/sms/features',
    title: 'SMS Feature Coverage',
    description:
      'Regulator-ready SMS feature coverage across web SMS workflows, public portal access, and a planned mobile app.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'SMS feature coverage',
      'aviation SMS features',
      'regulator-ready SMS',
      'safety workflow platform',
    ],
  },
  {
    path: '/sms-airlines',
    title: 'Airline Safety Management System Software',
    description:
      'Airline safety management system software for occurrence reporting, risk control, CAPA, SPIs, audits, and regulator-ready evidence.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'SMS for airlines',
      'airline safety management system',
      'airline safety reporting',
      'airline compliance evidence',
    ],
  },
  {
    path: '/sms-regulatory',
    title: 'SMS Software for Civil Aviation Authorities and ANSPs',
    description:
      'ICAO Annex 19 and Doc 9859 aligned SMS software for CAAs and ANSPs supporting audit-grade traceability, oversight workflows, and authority system-of-record governance.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'regulatory SMS',
      'authority SMS system of record',
      'SMS oversight',
      'Doc 9859',
    ],
  },
  {
    path: '/lms-aviation-authority',
    title: 'Aviation Authority LMS for Competency Governance',
    description:
      'Aviation authority LMS for competency governance, qualification evidence, recurrence control, and audit-ready oversight across regulated training programs.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'competency governance',
      'training evidence',
      'regulatory LMS',
      'ICAO competency',
    ],
  },
  {
    path: '/lms-ansp-caa',
    title: 'ANSP and CAA LMS for ATCO Training and Qualification',
    description:
      'Governed LMS for ANSP and CAA environments with ATCO objective management, qualification records, recurrence controls, audit-ready evidence, and authority oversight support.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'LMS for ANSP',
      'LMS for CAA',
      'aviation authority LMS',
      'air traffic control training',
      'ATCO training LMS',
      'ATCO training',
      'air navigation service provider training',
      'training organization oversight',
      'ATCO qualification management',
      'ANSP training management',
    ],
  },
  {
    path: '/lms-airlines',
    title: 'Airline LMS for Crew Qualification and Training Records',
    description:
      'Airline-focused LMS with crew qualification profiles, external training imports, check-event workflows, recurrence alerts, and compliance-ready training records.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'LMS for Airlines',
      'airline LMS',
      'airline crew training LMS',
      'crew qualification tracking',
      'airline training compliance',
      'pilot training management system',
    ],
  },
  {
    path: '/lms-aviation-companies',
    title: 'Aviation Company LMS for Training, Certification, and Compliance',
    description:
      'Aviation LMS for operators, MROs, ATOs, service providers, and regulated aviation businesses that need controlled training delivery, certification, qualification evidence, and partner access.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'aviation company LMS',
      'aviation training LMS',
      'regulated aviation training platform',
      'aviation compliance training',
      'aviation training records',
      'recurrent training aviation',
      'contractor training portal',
      'aviation certificate tracking',
    ],
  },
  {
    path: '/safety-governance',
    title: 'Aviation Safety Governance Software for National Authorities',
    description:
      'Unify SMS, competency, approvals, and oversight into one system of record for national aviation safety governance.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'safety governance',
      'system of record',
      'authority oversight',
      'national aviation safety',
    ],
  },
  {
    path: '/compliance',
    title: 'Aviation Compliance Software for ICAO Annex 15 and Annex 19',
    description:
      'Aviation compliance software aligned with ICAO Annex 15, ICAO Annex 19, EUROCONTROL eAIP standards, and aeronautical data quality requirements.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'ICAO Annex 15',
      'ICAO Annex 19',
      'EUROCONTROL specification',
      'aviation compliance',
    ],
    faq: COMPLIANCE_FAQ,
  },
  {
    path: '/pricing',
    title: 'Aviation Platform Pricing and Deployment Options',
    description:
      'Pricing and deployment options for aviation software, including SaaS, private cloud, and on-premise environments for authorities, ANSPs, airlines, and operators.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'civil aviation authority software',
      'ANSP platform pricing',
      'private cloud aviation',
      'on-premise aviation',
    ],
  },
  {
    path: '/team',
    title: 'Leadership Team',
    description:
      'Meet the AviaGov leadership team behind the enterprise aviation platform.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'aviation leadership',
      'compliance leadership',
      'safety governance',
      'air traffic management',
      'AviaGov team',
    ],
  },
  {
    path: '/solutions',
    title: 'Aviation Software Solutions for CAAs, ANSPs, Airlines, and Operators',
    description:
      'Aviation software solutions for Civil Aviation Authorities, ANSPs, airports, airlines, and regulated operators using SMS, LMS, eAIP, PANS-OPS, and governance workflows.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'aviation solutions',
      'CAA software',
      'ANSP software',
      'airline compliance platform',
      'regulated aviation organizations',
    ],
  },
  {
    path: '/about',
    title: 'About AviaGov',
    description:
      "Learn about the team behind the world's most advanced aviation management platform.",
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'aviation technology company',
      'civil aviation platform',
    ],
  },
  {
    path: '/blog',
    title: 'Aviation Insights Blog',
    description:
      'Stay updated with the latest trends in digital eAIP, aviation safety, and ICAO compliance.',
    ogType: 'article',
    keywords: [
      ...BASE_KEYWORDS,
      'aviation insights',
      'eAIP blog',
      'aeronautical information news',
    ],
  },
  {
    path: '/news',
    title: 'News',
    description:
      'Company news, product updates, and aviation technology announcements from AviaGov. Non-regulatory marketing news only.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'AviaGov news',
      'aviation technology updates',
      'company announcements',
    ],
  },
  {
    path: '/modules/utm',
    title: 'U-Space Governance Platform',
    description:
      'Sovereign regulatory oversight for UTM and U-Space ecosystems. Certification, SORA, audit and compliance infrastructure for authorities.',
    ogType: 'product',
    keywords: [
      ...BASE_KEYWORDS,
      'UTM platform',
      'U-Space governance',
      'SORA oversight',
      'drone regulatory platform',
      'USSP certification',
    ],
  },
  {
    path: '/public-portal',
    title: 'Public eAIP Portal',
    description:
      'The customer-facing digital publication portal for Civil Aviation Authorities.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'public eAIP portal',
      'digital AIP publication',
      'aeronautical information services',
    ],
  },
  {
    path: '/contact',
    title: 'Contact AviaGov',
    description:
      'Request a personalized demo of the AviaGov aviation platform or speak with our enterprise sales team.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'aviation platform demo',
      'enterprise aviation sales',
    ],
  },
  {
    path: '/privacy',
    title: 'Privacy Policy',
    description:
      'AviaGov data protection policy and privacy standards for aviation authorities.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'aviation data privacy',
      'GDPR aviation',
    ],
  },
  {
    path: '/terms',
    title: 'Terms of Service',
    description:
      'Legal terms and conditions for the usage of the AviaGov aviation platform.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'aviation platform terms',
    ],
  },
  {
    path: '/cookies',
    title: 'Cookie Policy',
    description:
      'Information about how AviaGov uses cookies to enhance platform performance.',
    ogType: 'website',
    keywords: [
      ...BASE_KEYWORDS,
      'cookie policy',
    ],
  },
];

export const DEFAULT_SEO = SEO_ROUTES[0];
